import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useUIStore } from "../../store/uiStore";
import { headerPalette } from "../../lib/headerTheme";

interface Props {
  ticks: string[];                   // ISO timestamps from /ticks
  activeTick: string | null;         // currently selected ISO
  onSelect: (tick: string) => void;
}

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Map an ISO tick to its YYYY-MM-DD bucket so we can look up "is this date available?"
function bucketKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function TickCalendar({ ticks, activeTick, onSelect }: Props) {
  const theme = useUIStore((s) => s.theme);
  const palette = headerPalette(theme);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [typed, setTyped] = useState<string>("");
  const [typedError, setTypedError] = useState<string | null>(null);
  const [popupRect, setPopupRect] = useState<{ top: number; left: number } | null>(null);

  // Lookup: bucket → ISO tick (one tick per day in this app).
  const bucketToTick = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of ticks) m.set(bucketKey(new Date(t)), t);
    return m;
  }, [ticks]);

  const sortedDates = useMemo(() => {
    return [...bucketToTick.keys()].sort();
  }, [bucketToTick]);

  const minDate = sortedDates[0];
  const maxDate = sortedDates[sortedDates.length - 1];

  const activeDate = activeTick ? new Date(activeTick) : null;
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    startOfMonth(activeDate ?? (maxDate ? new Date(maxDate) : new Date())),
  );

  // Re-anchor the visible month when the active tick changes from outside.
  useEffect(() => {
    if (activeDate) setViewMonth(startOfMonth(activeDate));
  }, [activeTick]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Click-outside to dismiss. Uses wrapperRef AND popupRect because the
  // popup is rendered with position:fixed (outside the wrapper subtree on
  // some browsers' stacking computations) — guard against clicking the
  // popup itself.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      // Defensive: if the click landed on the fixed-position popup, skip.
      if ((target as Element)?.closest?.('[data-tick-calendar-popup="1"]')) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  // Compute the popup's viewport coords from the trigger button so it
  // sits just below it. Recomputed when opening + on resize/scroll.
  useEffect(() => {
    if (!open) {
      setPopupRect(null);
      return;
    }
    const reposition = () => {
      const r = buttonRef.current?.getBoundingClientRect();
      if (!r) return;
      // Pin to the right edge if the popup would overflow the viewport.
      const popupW = Math.min(264, window.innerWidth - 24);
      let left = r.left;
      if (left + popupW > window.innerWidth - 12) {
        left = window.innerWidth - popupW - 12;
      }
      if (left < 12) left = 12;
      setPopupRect({ top: r.bottom + 6, left });
    };
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open]);

  const cells = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!open || !activeDate) return;
    const step: Record<string, number> = {
      ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7,
    };
    if (e.key in step) {
      e.preventDefault();
      const d = new Date(activeDate);
      d.setDate(d.getDate() + step[e.key]);
      const k = bucketKey(d);
      const tick = bucketToTick.get(k);
      if (tick) onSelect(tick);
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Enter") {
      setOpen(false);
    }
  };

  const buttonLabel = activeDate
    ? activeDate.toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      })
    : "Select date";

  const canPrev = minDate ? viewMonth > startOfMonth(new Date(minDate)) : true;
  const canNext = maxDate ? viewMonth < startOfMonth(new Date(maxDate)) : true;

  // Sync the text input with the active tick whenever the popup opens or
  // the active tick changes externally — keeps the field in lockstep.
  useEffect(() => {
    if (open) {
      setTyped(activeDate ? bucketKey(activeDate) : (maxDate ?? ""));
      setTypedError(null);
    }
  }, [open, activeTick]);   // eslint-disable-line react-hooks/exhaustive-deps

  const tryApplyTyped = (raw: string): void => {
    const v = raw.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      setTypedError("Use format YYYY-MM-DD");
      return;
    }
    const tick = bucketToTick.get(v);
    if (!tick) {
      const within = minDate && maxDate && v >= minDate && v <= maxDate;
      setTypedError(within ? "No data for that day" : `Out of range (${minDate} → ${maxDate})`);
      return;
    }
    setTypedError(null);
    onSelect(tick);
    setViewMonth(startOfMonth(new Date(v)));
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative", display: "inline-block" }} onKeyDown={onKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Pick a tick date"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px",
          fontSize: 13,
          color: palette.logo,
          background: palette.buttonBg,
          border: `1px solid ${palette.buttonBorder}`,
          borderRadius: 4,
          cursor: ticks.length === 0 ? "not-allowed" : "pointer",
          opacity: ticks.length === 0 ? 0.6 : 1,
        }}
        disabled={ticks.length === 0}
      >
        <CalendarIcon />
        <span>{buttonLabel}</span>
        <ChevronIcon open={open} />
      </button>

      {open && popupRect && createPortal(
        <div
          role="dialog"
          aria-label="Select tick date"
          data-tick-calendar-popup="1"
          style={{
            // Rendered via portal at document.body so the popup escapes the
            // AppHeader's stacking context (z-index 1000) and sits above
            // the leaflet zoom control (also z-index ~1000) reliably.
            position: "fixed",
            top: popupRect.top,
            left: popupRect.left,
            zIndex: 2000,
            width: 264,
            maxWidth: "calc(100vw - 24px)",
            padding: 10,
            background: palette.bg,
            color: palette.logo,
            border: `1px solid ${palette.buttonBorder}`,
            borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            fontSize: 12,
          }}
        >
          {/* Direct date entry */}
          <div style={{ marginBottom: 8 }}>
            <input
              type="text"
              inputMode="numeric"
              placeholder="YYYY-MM-DD"
              value={typed}
              min={minDate}
              max={maxDate}
              onChange={(e) => {
                setTyped(e.target.value);
                if (typedError) setTypedError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  tryApplyTyped(typed);
                }
              }}
              onBlur={() => {
                if (typed && typed !== bucketKey(activeDate ?? new Date(0))) {
                  tryApplyTyped(typed);
                }
              }}
              aria-invalid={typedError != null}
              style={{
                width: "100%",
                padding: "5px 8px",
                fontSize: 13,
                fontFamily: "ui-monospace, SFMono-Regular, monospace",
                color: palette.logo,
                background: palette.buttonBg,
                border: `1px solid ${typedError ? "#ef4444" : palette.buttonBorder}`,
                borderRadius: 4,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            {typedError && (
              <div style={{ marginTop: 4, fontSize: 11, color: "#ef4444" }}>
                {typedError}
              </div>
            )}
          </div>

          {/* Month nav */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, gap: 4 }}>
            <NavButton
              palette={palette}
              disabled={!canPrev}
              onClick={() => setViewMonth((m) => addMonths(m, -1))}
              ariaLabel="Previous month"
            >
              ‹
            </NavButton>
            <div style={{ fontWeight: 600, flex: 1, textAlign: "center" }}>
              {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </div>
            <NavButton
              palette={palette}
              disabled={!canNext}
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              ariaLabel="Next month"
            >
              ›
            </NavButton>
            {maxDate && (
              <button
                type="button"
                onClick={() => {
                  const tick = bucketToTick.get(maxDate);
                  if (tick) {
                    onSelect(tick);
                    setViewMonth(startOfMonth(new Date(maxDate)));
                  }
                }}
                title="Jump to latest available date"
                style={{
                  marginLeft: 4,
                  padding: "2px 8px",
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: theme === "dark" ? "#93c5fd" : "#1d4ed8",
                  background: "transparent",
                  border: `1px solid ${palette.buttonBorder}`,
                  borderRadius: 3,
                  cursor: "pointer",
                }}
              >
                Latest
              </button>
            )}
          </div>

          {/* Weekday header */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
            {WEEKDAYS.map((w, i) => (
              <div
                key={i}
                style={{
                  textAlign: "center",
                  fontSize: 10,
                  fontWeight: 600,
                  color: palette.statLabel,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {w}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {cells.map((cell, i) => {
              if (!cell) {
                return <div key={i} aria-hidden style={{ height: 28 }} />;
              }
              const k = bucketKey(cell);
              const tick = bucketToTick.get(k);
              const isAvailable = tick !== undefined;
              const isActive = activeDate ? bucketKey(activeDate) === k : false;
              const isToday = bucketKey(new Date()) === k;
              return (
                <DayCell
                  key={i}
                  cell={cell}
                  tick={tick}
                  isAvailable={isAvailable}
                  isActive={isActive}
                  isToday={isToday}
                  theme={theme}
                  palette={palette}
                  onPick={(t) => {
                    onSelect(t);
                    setOpen(false);
                  }}
                />
              );
            })}
          </div>

          <div style={{ marginTop: 8, fontSize: 10, color: palette.statLabel, lineHeight: 1.4 }}>
            {sortedDates.length} ticks · earliest {minDate} · latest {maxDate}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function DayCell({
  cell, tick, isAvailable, isActive, isToday, theme, palette, onPick,
}: {
  cell: Date;
  tick: string | undefined;
  isAvailable: boolean;
  isActive: boolean;
  isToday: boolean;
  theme: "light" | "dark";
  palette: ReturnType<typeof headerPalette>;
  onPick: (tick: string) => void;
}) {
  const [hover, setHover] = useState(false);
  const todayRingColor = theme === "dark" ? "#fbbf24" : "#b45309";

  return (
    <button
      type="button"
      onClick={() => { if (tick) onPick(tick); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={!isAvailable}
      aria-pressed={isActive}
      title={isAvailable
        ? cell.toDateString() + (isToday ? " (today)" : "")
        : `${cell.toDateString()} — no data` + (isToday ? " (today)" : "")}
      style={{
        position: "relative",
        height: 28,
        fontSize: 12,
        fontWeight: isActive ? 700 : isToday ? 600 : 500,
        color: isActive
          ? "#fff"
          : isAvailable
            ? palette.logo
            : palette.statLabel,
        background: isActive
          ? (theme === "dark" ? "#3b82f6" : "#1f2937")
          : hover && isAvailable
            ? (theme === "dark" ? "#374151" : "#e5e7eb")
            : "transparent",
        border: isActive
          ? "none"
          : isToday
            ? `1px solid ${todayRingColor}`
            : isAvailable
              ? `1px solid ${palette.buttonBorder}`
              : "1px dashed transparent",
        borderRadius: 4,
        cursor: isAvailable ? "pointer" : "default",
        opacity: isAvailable ? 1 : 0.35,
        padding: 0,
        transition: "background 80ms ease",
      }}
    >
      {cell.getDate()}
    </button>
  );
}

function buildMonthGrid(month: Date): (Date | null)[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const leadingBlanks = first.getDay();   // 0..6 (Sunday-first)
  const cells: (Date | null)[] = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), d));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function NavButton({
  children, onClick, disabled, ariaLabel, palette,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel: string;
  palette: ReturnType<typeof headerPalette>;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{
        width: 24,
        height: 24,
        fontSize: 16,
        lineHeight: "20px",
        background: palette.buttonBg,
        color: palette.logo,
        border: `1px solid ${palette.buttonBorder}`,
        borderRadius: 4,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

function CalendarIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden
      style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 120ms ease" }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
